import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersecting(true);
        observer.unobserve(element);
      }
    }, { threshold: 0.15, ...options });
    observer.observe(element);
    return () => { if (element) observer.unobserve(element); };
  }, [options]);

  return [elementRef, isIntersecting];
};

const AnimatedCounter = ({ target, duration = 1400, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useIntersectionObserver();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let start = 0;
      const increment = target / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) { setCount(target); clearInterval(timer); }
        else { setCount(Math.floor(start)); }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isVisible, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const SectionHeader = ({ title, subtitle }) => {
  const [ref, isVisible] = useIntersectionObserver();
  return (
    <div className={`section-header reveal-header ${isVisible ? 'visible' : ''}`} ref={ref}>
      <h2 className="section-title">{title}</h2>
      <p className="section-subtitle">{subtitle}</p>
    </div>
  );
};

const FloatingParticles = () => (
  <div className="floating-particles" aria-hidden="true">
    {[...Array(12)].map((_, i) => (
      <div key={i} className={`particle particle-${i + 1}`} />
    ))}
  </div>
);

const ProsperiaCRMShowcase = () => {
  const [ref, isVisible] = useIntersectionObserver();

  const features = [
    { icon: '📱', label: 'Mobile-First CRM' },
    { icon: '🤖', label: 'Prosperia AI Pricing' },
    { icon: '⚡', label: 'Estimates in 45 Seconds' },
    { icon: '👥', label: 'Crew Management' },
    { icon: '💳', label: 'Invoicing & Payments' },
    { icon: '📋', label: 'Job Management' },
  ];

  return (
    <div className={`prosperia-showcase ${isVisible ? 'visible' : ''}`} ref={ref}>
      <div className="showcase-badge">
        <span className="badge-dot" />
        Flagship Venture
      </div>

      <div className="showcase-header">
        <div className="showcase-logo-area">
          <img src="/prosperia-logo.png" alt="ProsperiaCRM logo" className="showcase-logo" />
          <div>
            <h2 className="showcase-title">ProsperiaCRM</h2>
            <p className="showcase-tagline">Complete Service Business Management</p>
          </div>
        </div>
        <a
          href="https://prosperiacrm.com"
          target="_blank"
          rel="noopener noreferrer"
          className="nav-button primary showcase-header-cta"
        >
          Visit ProsperiaCRM.com →
        </a>
      </div>

      <p className="showcase-description">
        The all-in-one mobile CRM purpose-built for pressure washing, roof cleaning, and exterior
        service professionals. Manage clients, jobs, invoices, and crews from a single platform —
        designed for the field, not the desk.
      </p>

      <div className={`showcase-features ${isVisible ? 'pills-visible' : ''}`}>
        {features.map((feature, index) => (
          <div
            key={index}
            className="showcase-feature-pill"
            style={{ transitionDelay: `${0.3 + index * 0.07}s` }}
          >
            <span className="pill-icon">{feature.icon}</span>
            {feature.label}
          </div>
        ))}
      </div>

      <div className="showcase-stats">
        <div className="showcase-stat">
          <div className="showcase-stat-value">45s</div>
          <div className="showcase-stat-label">Custom branded estimates</div>
        </div>
        <div className="showcase-stat-divider" />
        <div className="showcase-stat">
          <div className="showcase-stat-value">31</div>
          <div className="showcase-stat-label">Day free trial, no credit card</div>
        </div>
        <div className="showcase-stat-divider" />
        <div className="showcase-stat">
          <div className="showcase-stat-value">AI</div>
          <div className="showcase-stat-label">Powered job pricing engine</div>
        </div>
      </div>

      <div className="showcase-cta-row">
        <a
          href="https://prosperiacrm.com"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button primary"
        >
          Start Free Trial
        </a>
        <a
          href="https://prosperiacrm.com"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button secondary"
        >
          Learn More
        </a>
      </div>
    </div>
  );
};

const BeforehandLeadsShowcase = () => {
  const [ref, isVisible] = useIntersectionObserver();

  const features = [
    { icon: '🗂️', label: 'Public Records Intelligence' },
    { icon: '🏗️', label: 'Permits & Filings' },
    { icon: '🔎', label: 'A Reason Behind Every Lead' },
    { icon: '🔗', label: 'Delivered to Your CRM' },
    { icon: '🗺️', label: 'Free Signal Map' },
    { icon: '🧩', label: 'Built Around Your Business' },
  ];

  return (
    <div className={`prosperia-showcase ${isVisible ? 'visible' : ''}`} ref={ref}>
      <div className="showcase-badge">
        <span className="badge-dot" />
        Trade Name of Oasis Technologies LLC
      </div>

      <div className="showcase-header">
        <div className="showcase-logo-area">
          <div>
            <h2 className="showcase-title">Beforehand Leads</h2>
            <p className="showcase-tagline">Find the buyers who aren't in the databases</p>
          </div>
        </div>
        <a
          href="https://beforehandleads.com"
          target="_blank"
          rel="noopener noreferrer"
          className="nav-button primary showcase-header-cta"
        >
          Visit BeforehandLeads.com →
        </a>
      </div>

      <p className="showcase-description">
        We build systems that turn scattered public records — permits, filings, new businesses —
        into qualified leads in your CRM. Built around how you already sell, not a
        one-size-fits-all list.
      </p>

      <div className={`showcase-features ${isVisible ? 'pills-visible' : ''}`}>
        {features.map((feature, index) => (
          <div
            key={index}
            className="showcase-feature-pill"
            style={{ transitionDelay: `${0.3 + index * 0.07}s` }}
          >
            <span className="pill-icon">{feature.icon}</span>
            {feature.label}
          </div>
        ))}
      </div>

      <div className="showcase-stats">
        <div className="showcase-stat">
          <div className="showcase-stat-value">3</div>
          <div className="showcase-stat-label">Systems built for real sales work</div>
        </div>
        <div className="showcase-stat-divider" />
        <div className="showcase-stat">
          <div className="showcase-stat-value">30m</div>
          <div className="showcase-stat-label">Free signal map call</div>
        </div>
        <div className="showcase-stat-divider" />
        <div className="showcase-stat">
          <div className="showcase-stat-value">100%</div>
          <div className="showcase-stat-label">Public-record signals</div>
        </div>
      </div>

      <div className="showcase-cta-row">
        <a
          href="https://beforehandleads.com/#book"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button primary"
        >
          Book a Free Signal Map
        </a>
        <a
          href="https://beforehandleads.com"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button secondary"
        >
          Learn More
        </a>
      </div>
    </div>
  );
};

const InitiativeCard = ({ icon, title, subtitle, description, tags, backlink, delay = 0 }) => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <div
      className={`initiative-card ${isVisible ? 'visible' : ''}`}
      ref={ref}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="initiative-icon">{icon}</div>
      <h3 className="initiative-title">{title}</h3>
      <p className="initiative-subtitle">{subtitle}</p>
      <p className="initiative-description">{description}</p>
      <div className="initiative-tags">
        {tags.map((tag, i) => (
          <span key={i} className="initiative-tag" style={{ animationDelay: `${i * 0.05}s` }}>{tag}</span>
        ))}
      </div>
      {backlink && (
        <a
          href={backlink.href}
          target="_blank"
          rel="noopener noreferrer"
          className="initiative-backlink"
        >
          {backlink.label} →
        </a>
      )}
    </div>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const handleContactClick = () => { window.location.href = 'mailto:info@huntorca.com'; };

  return (
    <section className="cta-section">
      <div className={`cta-content reveal-cta ${isVisible ? 'visible' : ''}`} ref={ref}>
        <h2 className="cta-title">Partner With HuntOrca</h2>
        <p className="cta-subtitle">
          Whether you're looking to invest, collaborate, or just curious about what we're building — we'd love to connect.
        </p>
        <div className="cta-actions">
          <a
            href="https://prosperiacrm.com"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button primary large"
          >
            Try ProsperiaCRM Free
          </a>
          <button className="cta-button secondary large" onClick={handleContactClick}>
            Contact Us
          </button>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const handleContactClick = () => { window.location.href = 'mailto:info@huntorca.com'; };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="navbar-modern navbar-animate">
        <div className="navbar-content">
          <Link to="/" className="navbar-brand">
            <img src="/huntorca-logo.png" alt="HuntOrca Logo" className="brand-logo" />
            <span className="brand-name">HuntOrca</span>
          </Link>

          <div className="navbar-menu">
            <a href="#portfolio" className="nav-link">Portfolio</a>
            <a href="#initiatives" className="nav-link">Initiatives</a>
            <a href="mailto:info@huntorca.com" className="nav-link">Contact</a>
          </div>

          <div className="navbar-actions">
            <a
              href="https://prosperiacrm.com"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-button primary"
            >
              Visit ProsperiaCRM →
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-modern">
        <div className="hero-background">
          <div className="grid-pattern" />
          <div className="gradient-orb orb-1" />
          <div className="gradient-orb orb-2" />
          <div className="gradient-orb orb-3" />
          <FloatingParticles />
        </div>

        <div className="hero-content">
          <h1 className="hero-headline">
            Building Ventures
            <span className="gradient-text"> That Move Industries</span>
          </h1>

          <p className="hero-subheadline">
            HuntOrca is a holding company developing high-impact software products,
            data intelligence platforms, and emerging technology research.
          </p>

          <div className="hero-stats-row">
            <div className="hero-stat-item">
              <div className="hero-stat-value">
                <AnimatedCounter target={1} duration={800} />
              </div>
              <div className="hero-stat-label">Live Product</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat-item">
              <div className="hero-stat-value">
                <AnimatedCounter target={2} duration={1000} suffix="+" />
              </div>
              <div className="hero-stat-label">Active Initiatives</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat-item">
              <div className="hero-stat-value hero-stat-ai">AI</div>
              <div className="hero-stat-label">Native Across All Ventures</div>
            </div>
          </div>

          <div className="hero-actions">
            <a href="#portfolio" className="cta-button primary">
              Explore Our Portfolio
            </a>
            <button className="cta-button secondary" onClick={handleContactClick}>
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* ProsperiaCRM Showcase */}
      <section className="showcase-section" id="portfolio">
        <SectionHeader
          title="Our Portfolio"
          subtitle="Products built with precision, designed for the real world"
        />
        <div className="showcase-container">
          <ProsperiaCRMShowcase />
          <BeforehandLeadsShowcase />
        </div>
      </section>

      {/* Deep Tech Initiatives */}
      <section className="initiatives-section" id="initiatives">
        <SectionHeader
          title="Active Initiatives"
          subtitle="HuntOrca is at the frontier of applied research and emerging data intelligence"
        />

        <div className="initiatives-grid">
          <InitiativeCard
            delay={0}
            icon="⚗️"
            title="ML-Ready Chemical Data"
            subtitle="Structured Datasets for Chemical Intelligence"
            description="We are building machine learning–ready chemical datasets, enabling faster scientific discovery, materials research, and computational chemistry workflows. Our data pipeline expertise — the same infrastructure behind ProsperiaCRM's Prosperia AI — is being applied to high-complexity chemical intelligence."
            tags={['Chemical Data', 'ML Datasets', 'Scientific Research', 'Data Pipelines']}
            backlink={{ href: 'https://prosperiacrm.com', label: 'Explore Prosperia AI' }}
          />
        </div>
      </section>

      <CTASection />

      {/* Footer */}
      <footer className="footer-modern">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <img src="/huntorca-logo.png" alt="HuntOrca" className="footer-logo" />
              <p className="footer-tagline">
                Building ventures at the intersection of data, AI, and human expertise.
              </p>
              <p className="footer-tagline">
                <a href="https://beforehandleads.com" target="_blank" rel="noopener noreferrer">Beforehand Leads</a> is a trade name of Oasis Technologies LLC.
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Portfolio</h4>
                <a href="https://prosperiacrm.com" target="_blank" rel="noopener noreferrer">ProsperiaCRM</a>
                <a href="https://beforehandleads.com" target="_blank" rel="noopener noreferrer">Beforehand Leads</a>
              </div>

              <div className="footer-column">
                <h4>Initiatives</h4>
                <a href="#initiatives">ML Chemical Data</a>
              </div>

              <div className="footer-column">
                <h4>Company</h4>
                <a href="mailto:info@huntorca.com">Contact</a>
              </div>

              <div className="footer-column">
                <h4>Legal</h4>
                <a href="/privacy">Privacy Policy</a>
                <a href="/terms">Terms of Service</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} HuntOrca. All rights reserved.</p>
            <div className="footer-social">
              <a href="https://linkedin.com/company/huntorca" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a href="https://twitter.com/huntorca" aria-label="Twitter">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
